import { FileIcon, Loader2Icon } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { type DropzoneOptions, type FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../lib';
import { useToast } from '../use-toast';
import {
  acceptedExtensionsOf,
  fileExtension,
  findUnsupportedFile,
  formatList,
} from './document-formats';

export { DOCUMENT_AUTHORIZED_FORMATS } from './document-formats';

export interface FileDropzoneProps extends DropzoneOptions {
  className?: string;
  helpText?: ReactNode;
  onDrop(files: File[]): Promise<void> | void;
  isPending?: boolean;
  /** Minimum number of pages a dropped PDF must have. When set, PDFs with
   * fewer pages are rejected before upload — e.g. a signature anchored on a
   * fixed page needs the document to actually contain that page. */
  minPages?: number;
  children?: ReactNode;
}

export const FileDropzone = ({
  className,
  helpText,
  onDrop,
  isPending,
  multiple = false,
  minPages,
  children,
  ...props
}: FileDropzoneProps) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isUploading, setUploading] = useState(false);
  const inputId = useId();
  const acceptedExtensions = acceptedExtensionsOf(props.accept);
  const toastUnsupportedFormat = (fileName: string) =>
    toast({
      title: t('fileInput.unsupportedFormat', {
        extension: fileExtension(fileName),
        formats: formatList(acceptedExtensions),
      }),
      variant: 'destructive',
    });
  const { getInputProps, getRootProps, isDragActive, inputRef } = useDropzone({
    ...props,
    async onDrop(files: File[]) {
      // A consumed selection must be cleared so choosing the same file emits another change.
      if (inputRef.current) inputRef.current.value = '';
      setUploading(true);
      try {
        // `accept` passes a file whose MIME type matches even when its extension
        // isn't listed (Chrome reports .jfif as image/jpeg); the backend keys its
        // allowlist off the extension, so catch it here with a readable message.
        const unsupported = findUnsupportedFile(files, acceptedExtensions);
        if (unsupported) {
          toastUnsupportedFormat(unsupported.name);
          return;
        }
        if (minPages && minPages > 1) {
          // pdf-lib is heavy; only pull it in when a page minimum is enforced.
          const { PDFDocument } = await import('pdf-lib');
          for (const file of files) {
            if (file.type !== 'application/pdf') continue;
            try {
              const pageCount = (await PDFDocument.load(await file.arrayBuffer())).getPageCount();
              if (pageCount < minPages) {
                toast({
                  title: t('fileInput.tooFewPages', { minPages, pageCount }),
                  variant: 'destructive',
                });
                return;
              }
            } catch {
              // Unparseable PDF (e.g. encrypted) — let the upload/backend handle
              // it rather than blocking on a page count we couldn't read.
            }
          }
        }
        await onDrop(files);
      } catch (error) {
        toast({ title: 'Erreur', description: (error as Error).message });
      } finally {
        setUploading(false);
      }
    },
    onDropRejected(fileRejections: FileRejection[]) {
      const aiTooLarge = fileRejections
        .flatMap(rejection => rejection.errors)
        .find(error => error.code === 'ai-file-too-large');
      const invalidType = fileRejections.find(rejection =>
        rejection.errors.some(error => error.code === 'file-invalid-type'),
      );
      if (invalidType) {
        toastUnsupportedFormat(invalidType.file.name);
      }

      if (aiTooLarge) {
        toast({ title: aiTooLarge.message, variant: 'destructive' });
      } else {
        const hasFileTooLarge = fileRejections.some(rejection =>
          rejection.errors.some(error => error.code === 'file-too-large'),
        );
        if (hasFileTooLarge && props.maxSize) {
          const maxSizeMB = Math.round(props.maxSize / (1024 * 1024));
          toast({
            title: t('fileInput.fileTooLarge', { maxSize: maxSizeMB }),
            variant: 'destructive',
          });
        }
      }

      const hasTooManyFiles = fileRejections.some(rejection =>
        rejection.errors.some(error => error.code === 'too-many-files'),
      );
      if (hasTooManyFiles) {
        toast({
          title: t('fileInput.tooManyFiles'),
          variant: 'destructive',
        });
      }
    },
    multiple,
  });

  if (children) {
    return (
      <div {...getRootProps({ className, tabIndex: -1 })}>
        <input {...getInputProps({ id: inputId, className: 'sr-only' })} />
        {children}
      </div>
    );
  }

  return (
    <div {...getRootProps({ className })}>
      <div
        className={cn(
          'mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-4 md:px-6 py-3 md:py-4',
          isDragActive ? 'border-primary bg-gray-50' : 'border-gray-300',
        )}
      >
        <div className="text-center">
          {isUploading || isPending ? (
            <Loader2Icon
              className="mx-auto h-10 w-10 md:h-12 md:w-12 text-gray-300 animate-spin"
              aria-hidden="true"
            />
          ) : (
            <FileIcon
              className="mx-auto h-10 w-10 md:h-12 md:w-12 text-gray-300"
              aria-hidden="true"
            />
          )}
          <div className="mt-3 md:mt-4 flex flex-col md:flex-row items-center justify-center text-xs md:text-sm leading-5 md:leading-6 text-gray-600">
            <label
              htmlFor={inputId}
              // Native label↔input click association opens the file picker on
              // every browser (including Brave/Chromium with strict shields).
              // Stop propagation so react-dropzone's root onClick doesn't fire
              // a second `open()` and queue two pickers.
              onClick={event => event.stopPropagation()}
              className="relative cursor-pointer rounded-md bg-white font-semibold text-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-600 focus-within:ring-offset-2 hover:text-primary"
            >
              {multiple ? t('fileDropzone.selectFiles') : t('fileDropzone.selectFile')}
            </label>
            <input
              {...getInputProps({
                id: inputId,
                className: 'sr-only',
              })}
            />
            <p className="md:pl-1 mt-1 md:mt-0">{t('fileDropzone.orDragDrop')}</p>
          </div>
          {helpText && <p className="text-xs leading-5 text-gray-600 mt-2">{helpText}</p>}
        </div>
      </div>
    </div>
  );
};
